import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Switch, Route, Redirect, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import Content from '@/components/content'
import Icon from '@/components/icon'
import config from '@/config'
import { structure, breakpoints, colors } from '@/styles'
import CheckEmail from './SocialLogin/check-email'
import ExistingSocialUser from './SocialLogin/existing-social-user'
import LoginSocial from './SocialLogin/login-social'
import Password from './SocialLogin/password'
import ForgotPassword from './SocialLogin/forgot-password'
import ResetPasswordSuccess from './SocialLogin/reset-password-success'
import Context from './context'
import LandingBG from './landing-bg.png'

const { WEB_URL } = config

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url('${LandingBG}');
  background-repeat: no-repeat;
  background-position: bottom center;
  background-size: cover;
  min-height: 800px;
  height: 100vh;
  position: relative;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    height: unset;
    min-height: calc(100vh - ${structure.footer.height}px);
    align-items: flex-start;
    background-image: unset;
    background-color: ${props => (props.blue ? '#e9f2fc' : colors.grey96)};

    @supports (min-height: 100svh) {
      min-height: calc(100svh - ${structure.footer.height}px);
    }
  }
`

const LogoIcon = styled(Icon)`
  position: absolute;
  left: 40px;
  top: 20px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    left: 20px;
    top: 0px;
  }
`

const LoginContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 515px;
  margin: 100px 0px;
  max-width: 95%;
  background-color: ${colors.white};
  border-radius: 20px;
  border: 1px solid ${colors.grey88};
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.06), 0px 2px 4px -1px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    border-radius: 0;
    margin: 0;
    width: 100%;
    max-width: unset;
    box-shadow: unset;
    height: 100vh;
  }
`

const ContentContainer = styled(Content)`
  width: 100%;
  text-align: center;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 28px 16px;
  }
`

const Landing = () => {
  const [currentStep, setCurrentStep] = useState('login')
  const [social, setSocial] = useState()
  const [username, setUsername] = useState()
  const [email, setEmail] = useState()
  const [existingSocialState, setExistingSocialState] = useState(false)
  const [usernameClicked, setUsernameClicked] = useState(false)

  const components = {
    login: <LoginSocial />,
    password: <Password description="" />,
    forgotPassword: <ForgotPassword />,
    resetPasswordSuccess: <ResetPasswordSuccess />,
    checkEmail: <CheckEmail description="" />,
    existingSocialUser: <ExistingSocialUser description="" />
  }

  return (
    <LoginContainer>
      <Context.Provider
        value={{
          social,
          setSocial,
          username,
          setUsername,
          email,
          setEmail,
          existingSocialState,
          setExistingSocialState,
          currentStep,
          setCurrentStep,
          usernameClicked,
          setUsernameClicked
        }}
      >
        <ContentContainer top={28} left={20} right={20} bottom={28}>
          {components[currentStep]}
        </ContentContainer>
      </Context.Provider>
    </LoginContainer>
  )
}

const Layout = ({ children, blue }) => {
  const { t } = useTranslation()
  return (
    <Container blue={blue}>
      <Helmet>
        <title>{t('b.landing.view.login_signup.title', 'Log in - Sign up')}</title>
        <link rel="canonical" href="https://liv.rent/app" />
      </Helmet>
      <a href={WEB_URL}>
        <LogoIcon id="liv_logo" width={75} height={75} />
      </a>
      {children}
    </Container>
  )
}

const Main = () => {
  const location = useLocation()
  const redirectPath = location.pathname
  return (
    <Switch>
      <Route
        exact
        path="/"
        component={props => (
          <Layout blue>
            <Landing {...props} />
          </Layout>
        )}
      />
      <Route
        exact
        path="/listing/:listingId"
        render={props => {
          // this route will cause automatic redirecting to fail when user is linking to his own listing :(
          window.location = `${WEB_URL}/rental-listings/${props.match.params.listingId}`
          // display default landing page while redirect is in progress:
          return (
            <Layout blue>
              <Landing {...props} />
            </Layout>
          )
        }}
      />
      <Route path="/reset_password">
        <Redirect to="/" />
      </Route>
      <Route path="/callback">
        <Redirect to="/callback" />
      </Route>
      <Redirect to={`/?redirect=${encodeURIComponent(redirectPath)}`} />
    </Switch>
  )
}

export default Main
